import { exactText } from "@/lib/lossless"
import { isMasked } from "@/workspace/values"
import type { Obj } from "../data"
import {
  type Batch,
  type BankLine,
  type LedgerInvoice,
  type LedgerJournal,
  type LedgerPayment,
  type Payment,
  type StoreOrder,
  cairoDay,
  cairoDayOfInstant,
  dateOnly,
  decimal,
  piastres,
} from "./match"

/** The payments workbook's route id. */
export const PAYMENTS_WORKBOOK_ID = "payments-ledger"

/** Records a verdict on one payments finding. */
export const PAYMENT_DECIDE_ACTION = "recon.payment_match.decide"

/** The object types the payments workbook reads and writes. */
export const PAY_TYPES = {
  paymob: "paymob.Transaction",
  settlement: "paymob.Settlement",
  kashier: "kashier.PayoutLine",
  bank: "cib.StatementLine",
  store: "shopify.Order",
  invoice: "ledger.SalesInvoice",
  payment: "ledger.PaymentEntry",
  journal: "ledger.JournalEntry",
  decision: "recon.PaymentMatchDecision",
  sync: "recon.OrderSync",
} as const

function text(value: unknown): string | null {
  if (typeof value === "string") return value
  return exactText(value) ?? (typeof value === "number" ? String(value) : null)
}

function read(o: Obj) {
  const v = o.values as Record<string, unknown>
  const masked = o.masked as Record<string, unknown> | undefined
  return { v, hidden: (field: string) => isMasked(masked, field), get: (field: string) => (isMasked(masked, field) ? null : text(v[field])) }
}

const KIND: Record<string, Payment["kind"] | undefined> = { Payment: "capture", Refund: "refund", Chargeback: "chargeback" }

/** A Paymob transactions export row: piastre text, a Cairo wall-clock time, its settlement once paid out. */
export function fromPaymob(o: Obj): Payment {
  const { v, hidden, get } = read(o)
  const kind = KIND[text(v.type) ?? ""] ?? "capture"
  const sign = kind === "capture" ? 1 : -1
  const amount = piastres(text(v.amount_cents))
  const feeHidden = hidden("merchant_commission") || hidden("vat") || hidden("net_amount_cents")
  return {
    key: `Paymob:${o.key}`,
    provider: "Paymob",
    id: text(v.row_id) ?? o.key,
    kind,
    orderRef: text(v.merchant_order_id) ?? "",
    at: text(v.created_at) ?? "",
    day: cairoDay(text(v.created_at)),
    amountText: text(v.amount_cents) ?? "",
    gross: amount === null ? null : amount * sign,
    fee: piastres(get("merchant_commission")),
    vat: piastres(get("vat")),
    net: piastres(get("net_amount_cents")),
    feeHidden,
    method: text(v.source_data_type) ?? "",
    instrument: hidden("source_data_pan") ? null : [text(v.source_data_sub_type), get("source_data_pan")].filter(Boolean).join(" ·· "),
    instrumentHidden: hidden("source_data_pan"),
    batch: text(v.settlement_id),
    parent: text(v.parent_transaction_id),
    version: o.version,
  }
}

/** A Kashier payout report row: EGP text (signed), a UTC instant, the payout it belongs to. */
export function fromKashier(o: Obj): Payment {
  const { v, hidden, get } = read(o)
  const event = text(v.event) ?? ""
  return {
    key: `Kashier:${o.key}`,
    provider: "Kashier",
    id: text(v.transaction_id) ?? o.key,
    kind: event === "pay" ? "capture" : event === "refund" || event === "partial_refund" ? "refund" : "chargeback",
    orderRef: text(v.merchant_order_id) ?? "",
    at: text(v.transaction_date) ?? "",
    day: cairoDayOfInstant(text(v.transaction_date)),
    amountText: text(v.gross_amount) ?? "",
    gross: decimal(text(v.gross_amount)),
    fee: decimal(get("fees")),
    vat: decimal(get("vat")),
    net: decimal(get("net_amount")),
    feeHidden: hidden("fees") || hidden("vat") || hidden("net_amount"),
    method: text(v.method) ?? "",
    instrument: hidden("masked_card") ? null : [text(v.card_brand), get("masked_card")].filter(Boolean).join(" ·· "),
    instrumentHidden: hidden("masked_card"),
    batch: text(v.payout_id),
    parent: null,
    version: o.version,
  }
}

export function fromSettlement(o: Obj): Batch {
  const { v, hidden, get } = read(o)
  const reference = text(v.bank_reference)
  return {
    key: `Paymob:${o.key}`,
    provider: "Paymob",
    id: text(v.row_id) ?? o.key,
    date: dateOnly(text(v.settlement_date)),
    reportedNet: decimal(get("net_amount")),
    netHidden: hidden("net_amount"),
    bankReference: reference || null,
    transferred: text(v.status) === "Transferred",
    version: o.version,
  }
}

/** Kashier prints its payout's header on every line: one batch per payout id. */
export function kashierBatches(lines: readonly Obj[]): Batch[] {
  const out = new Map<string, Batch>()
  for (const o of lines) {
    const { v, hidden, get } = read(o)
    const id = text(v.payout_id) ?? ""
    const prior = out.get(id)
    const reference = text(v.bank_reference)
    out.set(id, {
      key: `Kashier:${id}`,
      provider: "Kashier",
      id,
      date: dateOnly(text(v.payout_date)),
      reportedNet: decimal(get("payout_net_amount")),
      netHidden: hidden("payout_net_amount"),
      bankReference: reference || null,
      transferred: Boolean(reference),
      // The batch changes whenever any of its lines does.
      version: (prior?.version ?? 0) + o.version,
    })
  }
  return [...out.values()]
}

export function fromBank(o: Obj): BankLine {
  const { v, hidden, get } = read(o)
  return {
    key: o.key,
    reference: text(v.row_id) ?? o.key,
    date: dateOnly(text(v.booking_date)),
    description: text(v.description) ?? "",
    credit: decimal(get("credit")),
    debit: decimal(get("debit")),
    amountHidden: hidden("credit") || hidden("debit"),
    version: o.version,
  }
}

/** The store order's numeric id (gid-shopify-Order-<id>), its name and the ERPNext order it names. */
export function fromStore(o: Obj): StoreOrder {
  const { v } = read(o)
  const gid = text(v.order_id) ?? o.key
  return { orderId: /(\d+)$/.exec(gid)?.[1] ?? gid, name: text(v.order_name) ?? gid, reference: text(v.erp_reference) }
}

const num = (value: unknown) => {
  const t = text(value)
  return t === null ? null : decimal(t)
}

export function fromInvoice(o: Obj): LedgerInvoice {
  const { v, get } = read(o)
  return {
    name: text(v.name) ?? o.key,
    customer: text(v.customer) ?? "",
    date: dateOnly(text(v.posting_date)),
    total: num(get("grand_total")),
    outstanding: num(get("outstanding_amount")),
    isReturn: num(v.is_return) === 1,
    returnAgainst: text(v.return_against),
    salesOrder: text(v.sales_order),
    submitted: num(v.docstatus) === 1,
    version: o.version,
  }
}

export function fromPaymentEntry(o: Obj): LedgerPayment {
  const { v, get } = read(o)
  return {
    name: text(v.name) ?? o.key,
    referenceNo: text(v.reference_no),
    mode: text(v.mode_of_payment),
    amount: num(get(text(v.payment_type) === "Receive" ? "received_amount" : "paid_amount")),
    invoice: text(v.invoice),
    date: dateOnly(text(v.posting_date)),
    submitted: num(v.docstatus) === 1,
    version: o.version,
  }
}

export function fromJournal(o: Obj): LedgerJournal {
  const { v, get } = read(o)
  return {
    name: text(v.name) ?? o.key,
    chequeNo: text(v.cheque_no),
    voucherType: text(v.voucher_type) ?? "",
    date: dateOnly(text(v.posting_date)),
    total: num(get("total_debit")),
    submitted: num(v.docstatus) === 1,
    version: o.version,
  }
}
